import { useState, useRef, useCallback } from "react";
import { XIcon } from "@phosphor-icons/react/X";
import type { ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Icon } from "../Icons/Icon";
import { PortalContextReset } from "../Controls/Internal/PortalContextReset";
import { usePopoverZIndex } from "../Theme/overlayZIndex";

export type FilterChipColor =
  | "blue"
  | "green"
  | "orange"
  | "purple"
  | "red"
  | "neutral";

const colorStyles: Record<
  FilterChipColor,
  { chip: string; label: string; dismiss: string }
> = {
  blue: {
    chip: "border-blue/30 bg-blue/5 text-blue-text",
    label: "text-blue-text/60",
    dismiss: "hover:bg-blue/10",
  },
  green: {
    chip: "border-green/30 bg-green/5 text-dark-green",
    label: "text-dark-green/60",
    dismiss: "hover:bg-green/10",
  },
  orange: {
    chip: "border-orange/30 bg-orange/5 text-orange",
    label: "text-orange/60",
    dismiss: "hover:bg-orange/10",
  },
  purple: {
    chip: "border-purple/30 bg-purple/5 text-purple",
    label: "text-purple/60",
    dismiss: "hover:bg-purple/10",
  },
  red: {
    chip: "border-red/30 bg-red/5 text-red",
    label: "text-red/60",
    dismiss: "hover:bg-red/10",
  },
  neutral: {
    chip: "border-border bg-muted text-content-secondary",
    label: "text-content",
    dismiss: "hover:bg-foreground/10",
  },
};

interface FilterChipProps {
  icon?: ReactNode;
  label: string;
  /**
   * The filter's value, shown after the label as `label: value`. Omit for a
   * valueless filter (e.g. a boolean toggle like "Freight Claims") — then only
   * the label renders, with no trailing colon.
   */
  value?: string | undefined;
  color?: FilterChipColor | undefined;
  popover?: ReactNode | undefined;
  onDismiss?: () => void;
  /** Fires when the chip body is clicked (not the dismiss X). */
  onClick?: (() => void) | undefined;
}

export function FilterChip({
  icon,
  label,
  value,
  color = "blue",
  popover,
  onDismiss,
  onClick,
}: FilterChipProps) {
  const styles = colorStyles[color];
  const clickable = onClick ? "cursor-pointer" : "";
  // a tighter, denser pill (1px border, less padding, smaller text) so the
  // filter chips read as control-bar chrome rather than chunky buttons.
  const chipSizing =
    "gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold";
  const chip = (
    <span
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`inline-flex items-center ${chipSizing} ${styles.chip} ${clickable}`}
    >
      {icon}
      {value ? (
        <>
          <span className={styles.label}>{label}:</span>
          <span>{value}</span>
        </>
      ) : (
        <span>{label}</span>
      )}
      {onDismiss && (
        <button
          type="button"
          aria-label={`Remove ${label} filter`}
          onClick={(e) => {
            // Don't let the X also fire the chip body's onClick.
            e.stopPropagation();
            onDismiss();
          }}
          // Negative margins keep the pill visually tight while giving the X a
          // comfortably clickable hit area — otherwise clicks miss and fall
          // through to the chip body, which opens the filter dialog instead.
          className={`-my-1 -mr-1 ml-0.5 rounded-full p-1.5 transition-colors ${styles.dismiss}`}
        >
          <Icon icon={XIcon} size="xs" />
        </button>
      )}
    </span>
  );

  if (!popover) return chip;

  return <FilterChipPopover_ trigger={chip}>{popover}</FilterChipPopover_>;
}

function FilterChipPopover_({
  trigger,
  children,
}: {
  trigger: ReactNode;
  children: ReactNode;
}) {
  const popoverZ = usePopoverZIndex();
  const [open, setOpen] = useState(false);
  const enterTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const leaveTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleEnter = useCallback(() => {
    clearTimeout(leaveTimeout.current);
    enterTimeout.current = setTimeout(() => setOpen(true), 300);
  }, []);

  const handleLeave = useCallback(() => {
    clearTimeout(enterTimeout.current);
    leaveTimeout.current = setTimeout(() => setOpen(false), 200);
  }, []);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <span onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
          {trigger}
        </span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          style={{ zIndex: popoverZ }}
          sideOffset={6}
          align="start"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          className="rounded-lg border border-border bg-card p-4 shadow-floating"
        >
          {/* Caller-supplied popover content must not inherit the trigger
              surface's layout contexts — see PortalContextReset. */}
          <PortalContextReset>{children}</PortalContextReset>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
